type Target = Record<string, (...args: any[]) => any>;

export type Stub<T extends Target> = {
  [K in keyof T]: T[K] extends (...args: infer A) => infer R
    ? (...args: A) => R extends Promise<any> ? R : Promise<R>
    : never;
};

const streams = new Map<string, ReadableStream<string>>();

function createStream(id: string, target: Window) {
  return new ReadableStream<Uint8Array<ArrayBuffer>>({
    start(controller) {
      const setreamHandler = (event: MessageEvent) => {
        if (
          event.isTrusted &&
          event.source === target &&
          event.data.id === id
        ) {
          if (event.data.error !== undefined) {
            window.removeEventListener("message", setreamHandler);
            controller.error(new Error(String(event.data.error)));
            streams.delete(id);
          } else if (event.data.result.done) {
            window.removeEventListener("message", setreamHandler);
            controller.close();
            streams.delete(id);
          } else {
            controller.enqueue(event.data.result.value);
          }
        }
      };

      window.addEventListener("message", setreamHandler);
    },
  });
}

export function connectClient<T extends Target>(
  name: string,
  targetOrigin: string,
  targetPromise: Window | Promise<Window>
): Stub<T> {
  return new Proxy(
    {},
    {
      get(_, fn) {
        return async (...args: any[]) => {
          const targetWindow = await targetPromise;
          return new Promise((resolve, reject) => {
            const id = `rpc:${Math.random().toString(36).slice(2)}`;
            const handler = (event: MessageEvent) => {
              if (
                event.isTrusted &&
                event.source === targetWindow &&
                event.data.id === id
              ) {
                window.removeEventListener("message", handler);
                if (event.data.error) {
                  reject(event.data.error);
                  return;
                }

                resolve(proxyStream(event.data.result, id, targetWindow));
              }
            };
            window.addEventListener("message", handler);
            targetWindow.postMessage({ id, name, fn, args }, targetOrigin);
          });
        };
      },
    }
  ) as Stub<T>;
}

function proxyStream(data: any, id: string, target: Window): any {
  if (data) {
    if (
      typeof data === "object" &&
      data.__stream__ === true &&
      typeof data.id === "string"
    ) {
      return createStream(data.id, target);
    }

    if (Array.isArray(data)) {
      return data.map((item: any) => proxyStream(item, id, target));
    }

    if (typeof data === "object") {
      return Object.fromEntries(
        Object.entries(data).map(([key, value]) => [
          key,
          proxyStream(value, id, target),
        ])
      );
    }
  }

  return data;
}
